<template>
  <ValidationObserver
    ref="validationObserver"
    v-slot="{ handleSubmit }"
  >
    <div class="breadcrumbs">
      <span class="system">
        Video Apps Engine / Banners /
      </span>
      <span v-if="!isEdit">
        Add Banner
      </span>
      <span
        v-else
      >
        Edit {{ banner ? getName(banner.name) : '' }}
      </span>
    </div>
    <form
      class="contact-info"
      novalidate="novalidate"
      @submit.prevent="handleSubmit(submit)"
      @input="handleFormChange"
    >
      <div class="title">
        {{ isEdit ? 'Banner Editor' : 'Add Banner' }}
      </div>
      <div class="edit-button-wrapper">
        <Button
          v-if="isEdit && banner"
          :title="'Delete'"
          :classType="'transparent'"
          :eventFunction="onOpenModal"
          :disabled="banner.status !== 'inactive'"
        />
        <Button
          :title="'Cancel'"
          :classType="'warning'"
          :eventFunction="backToCms"
        />
        <Button
          :title="isEdit ? 'Apply' : 'Add'"
          :classType="'primary'"
          :type="'submit'"
        />
      </div>
      <p class="section-title">
        General Info
      </p>
      <MaterialInput
        v-model="formData.label"
        v-bind="formFields.label"
      />
      <div class="select-wrapper">
        <MaterialSelect
          v-model="formData.itemType"
          v-bind="formFields.itemType"
          :options="optionsItemType"
          :withEmptyField="false"
        />
        <div
          v-if="shouldDisplayTargetTypeTooltip"
          class="tooltip tooltip-middle"
        >
          <inline-svg
            :src="iconTooltip"
            class="svg-icon"
          >
          </inline-svg>
          <span class="tooltiptext">
            If your app uses the Basic theme, selecting "Series Detail" or
            "Feature Detail" will display the content as a Playlist.
          </span>
        </div>
      </div>
      <div
        v-if="formData.itemType === 'page_link'"
        class="select-wrapper"
      >
        <MaterialSelect
          v-model="formData.pageTarget"
          v-bind="formFields.pageTarget"
          :options="subpages"
          :withEmptyField="false"
        />
      </div>
      <div
        v-if="formData.itemType === 'playlist_link'"
        style="margin-bottom: 20px"
      >
        <MaterialSelect
          v-model="formData.folderId"
          v-bind="formFields.folderId"
          label="Target"
          :required="true"
          :withEmptyField="false"
          :options="playlistsOptions"
        />
      </div>

      <div
        v-if="formData.itemType === 'series_link'"
        style="margin-bottom: 20px"
      >
        <MaterialSelect
          v-model="formData.seriesId"
          v-bind="formFields.seriesId"
          label="Target"
          :required="true"
          :withEmptyField="false"
          :options="seriesOptions"
        />
      </div>

      <div
        v-if="formData.itemType === 'feature_link'"
        style="margin-bottom: 20px"
      >
        <MaterialSelect
          v-model="formData.featureId"
          v-bind="formFields.featureId"
          label="Target"
          :required="true"
          :withEmptyField="false"
          :options="featuresOptions"
        />
      </div>

      <p class="section-title">
        V1 Banners
      </p>

      <div class="banner-image-wrapper">
        <div class="poster-title">
          Banner Image (3840х960)
          <span
            v-if="isThemeBasic"
            class="asterisk"
          >
            *
          </span>
        </div>
        <validation-provider
          ref="bannerImage"
          v-slot="{ errors }"
          :name="`${formPrefix}.bannerImage`"
          :rules="{ required: isThemeBasic && !formData.bannerImage }"
          :vid="`${formPrefix}.bannerImage`"
          mode="passive"
        >
          <PosterImage
            :key="formData.bannerImage"
            :poster="formData.bannerImage"
            :is4kPoster="true"
            :externalErrorMessage="errors[0] ? 'This field is required' : ''"
            :onPosterChange="handleBannerImageChange"
            :isTooltipeVisible="isThemeBasic"
            tooltipText="This Banner Image is available for Basic apps only."
          />
        </validation-provider>
        <span
          v-if="bannerImageError"
          class="error-label"
        >
          {{ bannerImageError }}
        </span>
      </div>
      <div class="banner-image-wrapper">
        <div class="poster-title">
          Banner Mobile Image (2880x960)
        </div>
        <validation-provider
          ref="mobileBannerImage"
          v-slot="{ errors }"
          :name="`${formPrefix}.mobileBannerImage`"
          :vid="`${formPrefix}.mobileBannerImage`"
          mode="passive"
        >
          <PosterImage
            :key="formData.mobileBannerImage"
            :poster="formData.mobileBannerImage"
            :is4kMobileBannerImage="true"
            :externalErrorMessage="errors[0] ? 'This field is required' : ''"
            :onPosterChange="handleMobileBannerImageChange"
            :isTooltipeVisible="isThemeBasic"
            tooltipText="This Banner Image is available for Basic apps only."
          />
        </validation-provider>
      </div>
      <div class="localization-wrapper">
        <div v-if="enabledLanguages.length">
          <Button
            :title="'Localization'"
            :classType="'primary'"
            :srcSvg="iconFolderLocalization"
            :svgClass="'folder-localization-icon'"
            :eventFunction="toLocalization('v1')"
            :isReverseIcon="true"
            :disabled="!isEdit"
          />
        </div>
      </div>
      <div
        v-if="!isEdit
          && enabledLanguages.length"
        class="localization-warning-wrapper"
      >
        <inline-svg
          :src="iconWarning"
          class="svg-icon"
        >
        </inline-svg>
        <span class="localization-warning">
          Localization will be available after the Banner has been saved
        </span>
      </div>

      <p class="section-title">
        V2 Banners
      </p>

      <template v-if="shouldDisplayToggle">
        <div class="toggle-wrapper">
          <div class="toggle-title">
            Show Metadata
          </div>
          <ToggleButton
            v-model="formData.showMetadata"
            color="#2591CF"
            :sync="true"
            :labels="false"
          />
          <div
            v-if="isThemeBasic"
            class="tooltip tooltip-middle-top"
          >
            <inline-svg
              :src="iconTooltip"
              class="svg-icon"
            >
            </inline-svg>
            <span class="tooltiptext">
              This setting has no effect in the Basic theme — metadata won’t appear on banners.
            </span>
          </div>
        </div>

        <div class="toggle-wrapper">
          <div class="toggle-title">
            Dynamic Image Source
          </div>
          <ToggleButton
            v-model="formData.dynamicImageSource"
            color="#2591CF"
            :sync="true"
            :labels="false"
          />
          <div
            v-if="isThemeBasic"
            class="tooltip tooltip-middle-top"
          >
            <inline-svg
              :src="iconTooltip"
              class="svg-icon"
            >
            </inline-svg>
            <span class="tooltiptext">
              This option is available for Media & Entertainment apps only.
              If you're using the Basic theme,
              banner metadata won't be displayed even if this toggle is ON.
            </span>
          </div>
        </div>
      </template>

      <template v-if="souldDisplayAdditionalBanners">
        <div class="banner-image-wrapper">
          <div class="poster-title">
            Banner Image (16:9)
            <span
              v-if="isThemeME"
              class="asterisk"
            >
              *
            </span>
          </div>
          <validation-provider
            ref="bannerImage16x9"
            v-slot="{ errors }"
            :name="`${formPrefix}.bannerImage16x9`"
            :rules="{ required: isThemeME && !formData.bannerImage16x9 }"
            :vid="`${formPrefix}.bannerImage16x9`"
            mode="passive"
          >
            <PosterImage
              :key="formData.bannerImage16x9"
              :poster="formData.bannerImage16x9"
              :externalErrorMessage="errors[0] ? 'This field is required' : ''"
              :onPosterChange="handleBannerImage16x9Change"
              :isTooltipeVisible="isThemeBasic"
              tooltipText="This Banner Image is available for Media & Entertainment apps only."
            />
          </validation-provider>
          <span
            v-if="bannerImage16x9Error"
            class="error-label"
          >
            {{ bannerImage16x9Error }}
          </span>
        </div>

        <div class="banner-image-wrapper banner-2x3-wrapper">
          <div class="poster-title">
            Banner Image (2:3)
            <span
              v-if="isThemeME"
              class="asterisk"
            >
              *
            </span>
          </div>
          <validation-provider
            ref="bannerImage2x3"
            v-slot="{ errors }"
            :name="`${formPrefix}.bannerImage2x3`"
            :rules="{ required: isThemeME && !formData.bannerImage2x3 }"
            :vid="`${formPrefix}.bannerImage2x3`"
            mode="passive"
          >
            <PosterImage
              :key="formData.bannerImage2x3"
              :poster="formData.bannerImage2x3"
              :externalErrorMessage="errors[0] ? 'This field is required' : ''"
              :onPosterChange="handleBannerImage2x3Change"
              :isTooltipeVisible="isThemeBasic"
              :isPoster2x3="true"
              tooltipText="This Banner Image is available for Media & Entertainment apps only."
            />
          </validation-provider>
          <span
            v-if="bannerImage2x3Error"
            class="error-label"
          >
            {{ bannerImage2x3Error }}
          </span>
        </div>

        <div class="localization-wrapper">
          <div v-if="enabledLanguages.length">
            <Button
              title="Localization"
              classType="primary"
              :srcSvg="iconFolderLocalization"
              svgClass="folder-localization-icon"
              :eventFunction="toLocalization('v2')"
              :isReverseIcon="true"
              :disabled="!isEdit"
            />
          </div>
        </div>
        <div
          v-if="!isEdit
            && enabledLanguages.length"
          class="localization-warning-wrapper"
        >
          <inline-svg
            :src="iconWarning"
            class="svg-icon"
          >
          </inline-svg>
          <span class="localization-warning">
            Localization will be available after the Banner has been saved
          </span>
        </div>
      </template>

      <NotificationModal
        ref="modal"
        v-model="isModalOpen"
        :modal="isModalOpen"
        :bodyMessage="bodyMessage"
        :title="'Delete Banner'"
        :onAccept="removeBanner"
        :onClose="onClose"
      />
      <BannerLocalization
        v-if="enabledLanguages.length"
        v-model="isOpenLocalization"
        :languages="enabledLanguages"
        :banner="banner"
        :modal="isOpenLocalization"
        :bannersType="bannerLocalizationType"
        :onClose="onCloseLocalizationModal"
      />
    </form>
  </ValidationObserver>
</template>

<script>
import {
  mapGetters, mapState,
} from 'vuex';
import { ValidationProvider } from 'vee-validate';
import CONFIG from '../../../constants/config';

import BaseForm from '../BaseForm.vue';
import MaterialInput from '../../inputs/MaterialInput.vue';
import Button from '../../common/Button.vue';
import CustomFieldFormMixin from '../customFields/CustomFieldFormMixin.vue';
import MaterialSelect from '../../inputs/MaterialSelect.vue';
import NotificationModal from '../../NotificationModal.vue';
import iconFolderLocalization from '../../../assets/icons/icon-folder-localization.svg';
import PosterImage from '../../common/PosterImage/DragAndDropFileUploader.vue';
import iconWarning from '../../../assets/icons/icon-warning-localization.svg';
import iconTooltip from '../../../assets/icons/icon-tooltip.svg';

import {
  CREATE_BANNER_REQUEST,
  EDIT_BANNER_REQUEST,
  GET_BANNER_REQUEST,
  GET_SUBPAGES_REQUEST,
  REMOVE_BANNER_REQUEST,
} from '../../../store/actions/cmsActions/cmsActions';
import { SET_EDIT_FORM, SET_OVERFLOW } from '../../../store/actions/common';
import { GET_LANGUAGES_REQUEST } from '../../../store/actions/localization/localizationActions';
import BannerLocalization from '../../localization/BannerLocalization.vue';
import { GET_PLAYLISTS_CMS_REQUEST } from '../../../store/actions/playlist/playlist';
import { GET_SERIES_CMS_REQUEST } from '../../../store/actions/series/series';
import { GET_FEATURES_CMS_REQUEST } from '../../../store/actions/features/features';

export default {
  name: 'BannerForm',
  components: {
    BannerLocalization,
    MaterialSelect,
    MaterialInput,
    Button,
    NotificationModal,
    PosterImage,
    ValidationProvider,
  },
  extends: BaseForm,
  mixins: [
    CustomFieldFormMixin,
  ],
  props: {
    formPrefix: {
      type: String,
      default: 'bannerForm',
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      playlistsOptions: [],
      seriesOptions: [],
      featuresOptions: [],
      optionsItemType: [
        {
          code: 'page_link',
          name: 'Page Link',
        },
        {
          code: 'playlist_link',
          name: 'Playlist Detail',
        },
        {
          code: 'series_link',
          name: 'Series Detail',
        },
        {
          code: 'feature_link',
          name: 'Feature Detail',
        },
      ],
      iconWarning,
      iconTooltip,
      iconFolderLocalization,
      isOpenLocalization: false,
      bannerLocalizationType: '',
      isModalOpen: false,
      bodyMessage: CONFIG.modals.deleteBannerInCms,
      rowsList: [],
      cmsPage: {},
      enabledLanguages: [],
      banner: null,
      bannerImageError: null,
      mobileBannerImageError: null,
      bannerImage16x9Error: null,
      bannerImage2x3Error: null,
      isBannerImageChanged: false,
      isMobileBannerImageChanged: false,
      isBannerImage16x9Changed: false,
      isBannerImage2x3Changed: false,
      foldError: '',
      formData: {
        label: '',
        itemType: '',
        pageTarget: '',
        bannerImage: '',
        mobileBannerImage: '',
        bannerImage16x9: '',
        bannerImage2x3: '',
        background: '',
        folderId: '',
        seriesId: '',
        featureId: '',
        sortOrder: 0,
        showMetadata: true,
        dynamicImageSource: false,
      },
      formFields: {
        label: {
          type: 'text',
          name: 'label',
          formPrefix: this.formPrefix,
          rules: {
            required: true,
            max: 255,
          },
        },
        itemType: {
          type: 'text',
          name: 'itemType',
          formPrefix: this.formPrefix,
          rules: {
            required: true,
          },
        },
        pageTarget: {
          type: 'text',
          name: 'pageTarget',
          formPrefix: this.formPrefix,
          rules: {
            required: true,
          },
        },
        folderId: {
          type: 'text',
          name: 'folderId',
          formPrefix: this.formPrefix,
          rules: {
            required: true,
            max: 25,
          },
        },
        seriesId: {
          type: 'text',
          name: 'seriesId',
          formPrefix: this.formPrefix,
          rules: {
            required: true,
            max: 25,
          },
        },
        featureId: {
          type: 'text',
          name: 'featureId',
          formPrefix: this.formPrefix,
          rules: {
            required: true,
            max: 25,
          },
        },
      },
      errorMapping: {
        label: [
          `${this.formPrefix}.name`,
        ],
        folderId: [
          `${this.formPrefix}.targetPlaylist`,
        ],
        seriesId: [
          `${this.formPrefix}.targetSeries`,
        ],
        featureId: [
          `${this.formPrefix}.targetFeature`,
        ],
        pageTarget: [
          `${this.formPrefix}.targetPage`,
        ],
        bannerImage: [
          `${this.formPrefix}.bannerImage`,
        ],
        mobileBannerImage: [
          `${this.formPrefix}.mobileBannerImage`,
        ],
        bannerImage16x9: [
          `${this.formPrefix}.bannerImage16x9`,
        ],
        bannerImage2x3: [
          `${this.formPrefix}.bannerImage2x3`,
        ],
      },
      submitEvent: this.isEdit ? EDIT_BANNER_REQUEST : CREATE_BANNER_REQUEST,
      successMessage: this.isEdit
        ? 'The Banner has been successfully updated'
        : 'The Banner has been successfully added',
      errorMessage: CONFIG.errorMessages.commonServerError,
    };
  },
  computed: {
    ...mapGetters([
      'getCurrentAccountId',
      'isThemeBasic',
      'isThemeME',
    ]),
    ...mapState({
      subpages: (state) => state.cms.subpages.map((item) => ({
        code: item.id,
        name: item.name,
      })),
    }),
    shouldDisplayTargetTypeTooltip() {
      return ['series_link', 'feature_link'].includes(this.formData.itemType) && this.isThemeBasic;
    },
    shouldDisplayToggle() {
      return ['series_link', 'feature_link', 'playlist_link'].includes(this.formData.itemType);
    },
    souldDisplayBannerImage16x9() {
      return this.formData.itemType === 'page_link'
        || (['series_link', 'feature_link', 'playlist_link'].includes(this.formData.itemType) && !this.formData.dynamicImageSource);
    },
    souldDisplayAdditionalBanners() {
      return this.formData.itemType === 'page_link'
        || (['series_link', 'feature_link', 'playlist_link'].includes(this.formData.itemType) && !this.formData.dynamicImageSource);
    },
  },
  mounted() {
    const paramsGet = {
      accountId: this.getCurrentAccountId,
    };
    this.$store.dispatch(GET_SUBPAGES_REQUEST, paramsGet);
    this.$store.dispatch(GET_PLAYLISTS_CMS_REQUEST, paramsGet).then((res) => {
      this.playlistsOptions = res.data.map((item) => ({
        name: `${item.title} (${item.id})`,
        code: item.id,
      }));
    });

    this.$store.dispatch(GET_SERIES_CMS_REQUEST, paramsGet).then((res) => {
      this.seriesOptions = res.data.map((item) => ({
        name: `${item.title} (${item.id})`,
        code: item.id,
      }));
    });

    this.$store.dispatch(GET_FEATURES_CMS_REQUEST, paramsGet).then((res) => {
      this.featuresOptions = res.data.map((item) => ({
        name: `${item.title} (${item.id})`,
        code: item.id,
      }));
    });

    const paramsLang = {
      accountId: this.getCurrentAccountId,
      page: 1,
      limit: 1000,
      filterValue: '',
    };
    this.$store.dispatch(GET_LANGUAGES_REQUEST, paramsLang).then((res) => {
      this.enabledLanguages = res.data;
    });

    if (this.isEdit && this.$route.params.id) {
      const params = {
        accountId: this.getCurrentAccountId,
        bannerId: this.$route.params.id,
      };

      this.$store.dispatch(GET_BANNER_REQUEST, params)
        .then((res) => this.initFormData(res));
    }
  },
  methods: {
    initFormData(item) {
      this.banner = item;
      this.formData.label = item.name;
      this.formData.itemType = item.type;

      this.formData.showMetadata = item.showMetadata;
      this.formData.dynamicImageSource = item.dynamicImageSource;

      if (item.type === 'page_link') {
        this.formData.pageTarget = item.targetPage.id;
      }

      if (item.type === 'playlist_link') {
        this.formData.folderId = item.targetPlaylist.id;
      }

      if (item.type === 'series_link') {
        this.formData.seriesId = item.targetSeries.id;
      }

      if (item.type === 'feature_link') {
        this.formData.featureId = item.targetFeature.id;
      }

      this.formData.bannerImage = item.bannerImage ? `${item.bannerImage}?height=360` : '';
      this.formData.mobileBannerImage = item.mobileBannerImage ? `${item.mobileBannerImage}?height=360` : '';
      this.formData.bannerImage16x9 = item.bannerImage16x9 ? `${item.bannerImage16x9}?height=360` : '';
      this.formData.bannerImage2x3 = item.bannerImage2x3 ? `${item.bannerImage2x3}?height=360` : '';
    },
    handleBannerImageChange(img) {
      this.isBannerImageChanged = true;
      this.$store.dispatch(SET_EDIT_FORM, true);
      this.formData.bannerImage = img;
      this.$refs.bannerImage?.reset();

      if (this.bannerImageError) {
        this.bannerImageError = false;
      }
    },
    handleMobileBannerImageChange(img) {
      this.isMobileBannerImageChanged = true;
      this.$store.dispatch(SET_EDIT_FORM, true);
      this.formData.mobileBannerImage = img;
      this.$refs.mobileBannerImage?.reset();

      if (this.mobileBannerImageError) {
        this.mobileBannerImageError = false;
      }
    },
    handleBannerImage16x9Change(img) {
      this.isBannerImage16x9Changed = true;
      this.$store.dispatch(SET_EDIT_FORM, true);
      this.formData.bannerImage16x9 = img;
      this.$refs.bannerImage16x9?.reset();

      if (this.bannerImage16x9Error) {
        this.bannerImage16x9Error = false;
      }
    },
    handleBannerImage2x3Change(img) {
      this.isBannerImage2x3Changed = true;
      this.$store.dispatch(SET_EDIT_FORM, true);
      this.formData.bannerImage2x3 = img;
      this.$refs.bannerImage2x3?.reset();

      if (this.bannerImage2x3Error) {
        this.bannerImage2x3Error = false;
      }
    },
    toLocalization(type) {
      return () => {
        this.bannerLocalizationType = type;
        this.isOpenLocalization = true;
      };
    },
    handleRecorderToggle() {
      if (!this.formData.isToggleRecord) {
        this.isRecorderToggleOpen = true;
      }
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    backToCms() {
      this.$router.push('/cms/banners');
    },
    getName(name) {
      return name.length > 45 ? `${name.slice(0, 45)}...` : name;
    },
    onOpenModal() {
      this.isModalOpen = true;
      this.$store.dispatch(SET_OVERFLOW, true);
    },
    onClose() {
      this.isModalOpen = false;
      this.$store.dispatch(SET_EDIT_FORM, false);
    },
    onCloseLocalizationModal() {
      this.isOpenLocalization = false;
    },
    onFoldersChange(value) {
      this.formData.folderId = value ? value.code : value;
      this.foldError = '';
    },
    removeBanner() {
      const params = {
        accountId: this.getCurrentAccountId,
        bannerId: this.$route.params.id,
      };

      this.$store.dispatch(REMOVE_BANNER_REQUEST, params)
        .then(() => {
          setTimeout(() => {
            this.backToCms();
          }, CONFIG.routing.redirectFromAddAcc);
          this.$toasted.global.success({
            message: 'The Banner has been successfully deleted',
          });
        })
        .catch((err) => {
          if (err.status === CONFIG.statuses.failedStatus) {
            this.$toasted.global.error({
              message: CONFIG.errorMessages.commonServerError,
            });
          }
        });
    },
    getRequestData() {
      const data = {
        name: this.formData.label,
        type: this.formData.itemType,
        dataSource: this.formData.contentType,
        showMetadata: this.formData.showMetadata,
        dynamicImageSource: this.formData.dynamicImageSource,
      };

      if (this.formData.itemType === 'page_link') {
        data.targetPage = this.formData.pageTarget;
        data.showMetadata = false;
      }

      if (this.formData.itemType === 'playlist_link') {
        data.targetPlaylist = this.formData.folderId;
      }

      if (this.formData.itemType === 'series_link') {
        data.targetSeries = this.formData.seriesId;
      }

      if (this.formData.itemType === 'feature_link') {
        data.targetFeature = this.formData.featureId;
      }

      if (this.isBannerImageChanged) {
        data.bannerImage = this.formData.bannerImage;
      }

      if (this.isMobileBannerImageChanged) {
        data.mobileBannerImage = this.formData.mobileBannerImage;
      }

      if (this.isBannerImage16x9Changed) {
        data.bannerImage16x9 = this.formData.bannerImage16x9;
      }

      if (this.isBannerImage2x3Changed) {
        data.bannerImage2x3 = this.formData.bannerImage2x3;
      }

      return {
        data,
        accountId: this.getCurrentAccountId,
        bannerId: this.$route.params.id,
      };
    },
    onSubmitSuccess() {
      this.$toasted.global.success({
        message: this.successMessage,
      });
      setTimeout(() => {
        this.backToCms();
      }, CONFIG.routing.redirectFromAddAcc);
    },
    onSubmitFail(error) {
      const formErrors = this.getServerFormErrors(error);
      this.posterError = error.form_errors.children
        && error.form_errors.children.bannerImage
        && error.form_errors.children.bannerImage.errors;

      const folderError = error.form_errors
        && error.form_errors.children
        && error.form_errors.children.targetPlaylist
        && error.form_errors.children.targetPlaylist.errors
        && error.form_errors.children.targetPlaylist.errors[0].error;

      if (folderError) {
        this.foldError = folderError === 'NOT_SYNCHRONIZED_ERROR'
          ? 'The ID entered does not exist'
          : 'This field is required';
      }

      if (formErrors.pageTarget) {
        formErrors.pageTarget[0] = 'This value is already used as a Navigation Item';
      }

      if (Object.entries(formErrors).length && this.$refs[this.validationObserver]) {
        this.$refs[this.validationObserver].setErrors(formErrors);
      } else {
        this.$toasted.global.error({
          message: this.errorMessage,
        });
      }
    },
  },
};
</script>

<style scoped>

.title {
  color: #4c5057;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 0x;
}

.section-title {
  font-weight: 400;
  font-style: italic;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0px;
  color: #4C5057;
  margin-top: 30px;
  margin-bottom: 30px;
}

.rows {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.toggles-wrapper {
  display: flex;
}

.toggle-wrapper,
.date-wrapper {
  align-items: center;
  display: flex;
  margin-right: 30px;
  width: 17vw;
}

.toggle-wrapper {
  width: auto;
}

.toggle-title {
  margin-right: 10px;
}

.dynamic {
  margin-top: 30px;
  margin-bottom: 30px;
}

.edit-button-wrapper {
  position: absolute;
  top: 75px;
  display: flex;
  right: 30px;
}

.country-field {
  width: 300px;
}

.country-field:not(:last-child) {
  margin-right: 20px;
}

.wowza p:last-child {
  background: #383b40;
  margin-top: 10px;
  padding: 15px;
}

.poster-title {
  color: #fff;
  font-weight: normal;
  margin-bottom: 10px;
}

.banner-image-wrapper {
  margin-bottom: 20px;
  position: relative;
}

.banner-2x3-wrapper {
  max-width: 300px;
}

.wrapper-small {
  margin-bottom: 20px;
}

.flex {
  justify-content: space-between;
}

.person {
  margin-bottom: 20px;
}

.person p:last-child {
  background: #383b40;
  margin-top: 10px;
  padding: 15px;
}

.select-wrapper {
  width: 100%;
  position: relative;
}

.system {
  color: #4c5057;
}

.breadcrumbs {
  margin-bottom: 25px;
}

.localization-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  margin-bottom: 30px;
}

.label {
  width: 400px;
}

.localization-warning-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #F2C94C;
  border-radius: 4px;
  padding: 10px;
  margin-left: 2px;
  margin-bottom: 20px;
}

.localization-warning {
  font-size: 14px;
  color: #F2C94C;
  padding-left: 8px;
}

.select-wrapper-folder {
  width: 100%;
}

.rows-folder {
  display: block;
}

.asterisk {
  color: #fb3951;
}

@media all and (max-width: 1320px) {
  .flex {
    flex-wrap: wrap;
  }

  .country-field {
    margin-right: 0;
    width: 100%;
  }

  .country-field:not(:last-child) {
    margin-right: 0;
  }
}

.tooltip {
  position: absolute;
  display: inline-block;
  right: -36px;
}

.tooltip-middle {
  top: 42px;
}

.tooltip-top {
  top: 30px;
}

.tooltip-middle-top {
  top: 2px;
}

.tooltip .tooltiptext {
  visibility: hidden;
  width: 220px;
  top: 100%;
  left: 50%;
  margin-left: -90px;
  background-color: #fff;
  color: #000;
  padding: 4px 6px;
  border-radius: 1px;
  font-size: 13px;

  position: absolute;
  z-index: 1;
}

.tooltip:hover .tooltiptext {
  visibility: visible;
}

.toggle-wrapper {
  margin-bottom: 30px;
  position: relative;
  width: fit-content;
}
</style>
